import React, { useEffect, useState } from 'react';
import {
  useGetPublicCoursesQuery,
  useGetAllCategoriesQuery,
} from '../../api/apiSlice';
import CourseCard from '../../components/course/CourseCard';
import Pagination from '../../components/common/Pagination';
import {
  Search,
  Filter,
  X,
  SlidersHorizontal,
  BookOpen,
} from 'lucide-react';

const PAGE_SIZE = 12;

const initialFilters = {
  category: '',
  language: '',
  minPrice: '',
  maxPrice: '',
  sortBy: 'createdAt',
  search: '',
};

// Простой debounce, чтобы не дёргать сеть на каждом нажатии в поиске.
const useDebounced = (value, delay = 350) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
};

const CourseList = () => {
  const [filters, setFilters] = useState(initialFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebounced(filters.search, 350);

  // Любая смена фильтров возвращает на первую страницу.
  useEffect(() => {
    setPage(1);
  }, [filters.category, filters.language, filters.minPrice, filters.maxPrice, filters.sortBy, debouncedSearch]);

  const {
    data,
    error: coursesError,
    isLoading: coursesLoading,
    isFetching,
  } = useGetPublicCoursesQuery({
    category: filters.category || undefined,
    language: filters.language || undefined,
    minPrice: filters.minPrice || undefined,
    maxPrice: filters.maxPrice || undefined,
    sortBy: filters.sortBy,
    search: debouncedSearch || undefined,
    page,
    limit: PAGE_SIZE,
  });

  const {
    data: categories,
    error: categoriesError,
    isLoading: categoriesLoading,
  } = useGetAllCategoriesQuery();

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pageCount = data?.pageCount ?? 1;

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => setFilters(initialFilters);

  const hasActiveFilters =
    filters.category ||
    filters.language ||
    filters.minPrice ||
    filters.maxPrice ||
    filters.search ||
    filters.sortBy !== 'createdAt';

  const getCourseWord = (count) => {
    if (count % 10 === 1 && count % 100 !== 11) return 'курс';
    if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'курса';
    return 'курсов';
  };

  if (coursesLoading) {
    // Skeleton каркас каталога — perceived-perf лучше чем центральный спиннер.
    // Совпадает по стилю с InstructorProfileView/MyCourses: серые блоки с
    // animate-pulse, 8 заглушек карточек в grid'е как при реальном контенте.
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="animate-pulse space-y-6">
          {/* Header skeleton */}
          <div className="space-y-3">
            <div className="h-6 w-32 rounded-full bg-gray-200 dark:bg-gray-800" />
            <div className="h-9 w-72 max-w-full rounded bg-gray-200 dark:bg-gray-800" />
            <div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-800" />
          </div>
          {/* Card grid skeleton */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="rounded-3xl border border-gray-200/70 dark:border-gray-700/70 bg-white dark:bg-gray-900 overflow-hidden"
              >
                <div className="aspect-video bg-gray-200 dark:bg-gray-800" />
                <div className="p-5 space-y-3">
                  <div className="h-5 w-3/4 rounded bg-gray-200 dark:bg-gray-800" />
                  <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-800" />
                  <div className="h-4 w-2/3 rounded bg-gray-200 dark:bg-gray-800" />
                  <div className="flex gap-2 pt-1">
                    <div className="h-6 w-16 rounded-full bg-gray-200 dark:bg-gray-800" />
                    <div className="h-6 w-20 rounded-full bg-gray-200 dark:bg-gray-800" />
                  </div>
                  <div className="h-8 w-24 rounded-full bg-gray-200 dark:bg-gray-800" />
                  <div className="h-12 w-full rounded-2xl bg-gray-200 dark:bg-gray-800" />
                  <div className="h-10 w-full rounded-2xl bg-gray-200 dark:bg-gray-800" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (coursesError) {
    return (
      <section className="min-h-[60vh] px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[420px]">
          <div className="w-full max-w-lg rounded-3xl border border-red-100 dark:border-red-900/40 bg-white dark:bg-gray-900 shadow-xl p-8 text-center">
            <div className="flex justify-center mb-5">
              <div className="p-4 rounded-full bg-red-100 dark:bg-red-500/10">
                <X className="w-10 h-10 text-red-500" />
              </div>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Не удалось загрузить курсы
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {coursesError?.data?.message || 'Произошла ошибка при загрузке курсов.'}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center px-5 py-3 rounded-xl bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors shadow-md"
            >
              Обновить страницу
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between mb-8">
          <div className="min-w-0">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
              Найдите курс для себя
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400 text-sm sm:text-base">
              Найдено {total} {getCourseWord(total)}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative w-full sm:w-[320px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                name="search"
                placeholder="Поиск по названию или описанию..."
                value={filters.search}
                onChange={handleFilterChange}
                className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm"
              />
            </div>

            <button
              onClick={() => setShowFilters((p) => !p)}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors shadow-sm"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mb-8 rounded-3xl border border-gray-200 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90 backdrop-blur shadow-md p-4 sm:p-6">
            <div className="flex items-center gap-2 mb-5">
              <Filter className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Фильтры</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-6 gap-4">
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Категория</label>
                <select
                  name="category"
                  value={filters.category}
                  onChange={handleFilterChange}
                  disabled={categoriesLoading}
                  className="px-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Все категории</option>
                  {categories?.map((cat) => (
                    <option key={cat._id} value={cat._id}>{cat.title}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Язык</label>
                <select
                  name="language"
                  value={filters.language}
                  onChange={handleFilterChange}
                  className="px-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Все языки</option>
                  <option value="kk">Қазақша</option>
                  <option value="ru">Русский</option>
                  <option value="en">English</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Цена от</label>
                <input
                  type="number"
                  name="minPrice"
                  placeholder="0"
                  value={filters.minPrice}
                  onChange={handleFilterChange}
                  className="px-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Цена до</label>
                <input
                  type="number"
                  name="maxPrice"
                  placeholder="100000"
                  value={filters.maxPrice}
                  onChange={handleFilterChange}
                  className="px-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Сортировка</label>
                <select
                  name="sortBy"
                  value={filters.sortBy}
                  onChange={handleFilterChange}
                  className="px-4 py-3 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="createdAt">Новые сначала</option>
                  <option value="price_asc">Цена: по возрастанию</option>
                  <option value="price_desc">Цена: по убыванию</option>
                  <option value="popular">Популярные</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Действия</label>
                <button
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-300 border border-red-100 dark:border-red-900/40 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-4 h-4" />
                  Очистить
                </button>
              </div>
            </div>

            {categoriesError && (
              <p className="mt-4 text-sm text-red-500">Не удалось загрузить категории</p>
            )}
          </div>
        )}

        {/* Content */}
        {items.length > 0 ? (
          <>
            <div
              className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6 transition-opacity ${
                isFetching ? 'opacity-60' : ''
              }`}
            >
              {items.map((course) => (
                <CourseCard key={course._id} course={course} />
              ))}
            </div>

            <div className="mt-10">
              <Pagination
                page={page}
                pageCount={pageCount}
                onChange={(p) => {
                  setPage(p);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            </div>
          </>
        ) : (
          <div className="rounded-3xl border border-dashed border-gray-300 dark:border-gray-700 bg-white/70 dark:bg-gray-900/70 p-10 sm:p-14 text-center shadow-sm">
            <div className="flex justify-center mb-5">
              <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800">
                <Search className="w-10 h-10 text-gray-400" />
              </div>
            </div>
            <h3 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Курсы не найдены
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
              Попробуйте изменить параметры поиска, выбрать другую категорию или сбросить фильтры.
            </p>
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors shadow-md"
            >
              <X className="w-4 h-4" />
              Сбросить фильтры
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default CourseList;
